// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Errors from reading a repository.
 *
 * Every failure mode here has a *specific* cause the user can act on, and a
 * generic "network error" would waste their time. In particular a browser
 * cannot tell a CORS refusal apart from an unreachable host — both surface as
 * the same `TypeError` — so that message names both possibilities rather than
 * guessing at one.
 */

export type RepoErrorKind =
  | 'bad-url'
  | 'unsupported-host'
  | 'unreachable'
  | 'not-found'
  | 'private'
  | 'rate-limited'
  | 'no-boards'
  | 'lfs'
  | 'not-a-board'
  | 'http';

export class RepoAccessError extends Error {
  readonly kind: RepoErrorKind;
  /** A concrete next step, shown under the message. */
  readonly hint: string;
  readonly status?: number;

  constructor(kind: RepoErrorKind, message: string, hint: string, status?: number) {
    super(message);
    this.name = 'RepoAccessError';
    this.kind = kind;
    this.hint = hint;
    if (status !== undefined) this.status = status;
  }
}

export function badUrl(input: string): RepoAccessError {
  return new RepoAccessError(
    'bad-url',
    `"${input.trim().slice(0, 120)}" is not a repository URL.`,
    'Paste the repository address, for example https://github.com/owner/project, ' +
      'https://gitlab.com/group/subgroup/project or https://codeberg.org/owner/project. ' +
      'A link to a file or a commit works too.',
  );
}

export function unsupportedHost(host: string): RepoAccessError {
  return new RepoAccessError(
    'unsupported-host',
    `Could not work out what software is running at ${host}.`,
    'GitHub, GitLab and Gitea/Forgejo (Codeberg) are supported, including self-hosted GitLab and ' +
      'Forgejo. The host answered neither a GitLab nor a Gitea API probe — check the address, or that ' +
      'the repository is public.',
  );
}

/**
 * A failed `fetch`. In a browser this is the same `TypeError` whether the host
 * refused the cross-origin read, the DNS lookup failed, or the machine is
 * offline — so all three are named.
 */
export function unreachable(host: string, provider?: string): RepoAccessError {
  // The Gitea note is included whenever it might apply — for a known Gitea
  // host, and for an unidentified one, since a self-hosted Forgejo is the most
  // common case where a single config change fixes this.
  const gitea =
    provider === 'gitea'
      ? ' Self-hosted Gitea and Forgejo block browser requests by default: an administrator has to set ' +
        '`ENABLED = true` under `[cors]` in app.ini.'
      : provider === undefined
        ? ' If that host runs Gitea or Forgejo, note that they block browser requests by default (CORS): ' +
          'an administrator has to set `ENABLED = true` under `[cors]` in app.ini.'
        : '';
  return new RepoAccessError(
    'unreachable',
    `The browser could not read from ${host}.`,
    `Either that host does not allow cross-origin reads from a web page, or it could not be reached ` +
      `at all — a browser reports both the same way, so this cannot be narrowed down from here.${gitea} ` +
      `Check the address is right and that you can open it in a tab. If the host is behind a VPN or a ` +
      `login, the tool cannot reach it and you will need to download the files by hand.`,
  );
}

export function notFound(what: string): RepoAccessError {
  return new RepoAccessError(
    'not-found',
    `${what} was not found.`,
    'Check the spelling, and note that only public repositories can be read — a private repository ' +
      'looks exactly like a missing one from outside.',
    404,
  );
}

export function privateRepo(host: string): RepoAccessError {
  return new RepoAccessError(
    'private',
    `${host} refused the request.`,
    'This tool only reads public repositories and never sends credentials. If the repository is ' +
      'private, download the two revisions yourself and load them as local files.',
    403,
  );
}

export function rateLimited(host: string, resetAt?: Date): RepoAccessError {
  const when = resetAt ? ` The limit resets at ${resetAt.toLocaleTimeString()}.` : '';
  return new RepoAccessError(
    'rate-limited',
    `${host} is rate-limiting anonymous requests.`,
    `Public API access is capped per IP address — GitHub allows 60 requests an hour without a login.${when} ` +
      `Wait for the reset, or load the two files locally in the meantime.`,
    403,
  );
}

export function noBoards(ref: string, searched: number, truncated: boolean): RepoAccessError {
  if (truncated) {
    // Saying "there is no board here" would be a claim we cannot support.
    return new RepoAccessError(
      'no-boards',
      `No .kicad_pcb file was found in the first ${searched.toLocaleString()} entries on "${ref}", ` +
        `and the listing was cut off before the end.`,
      'This repository is too large to enumerate through the host API. Paste a link straight to the ' +
        'board file instead — the address of the file on the host works as input.',
    );
  }
  return new RepoAccessError(
    'no-boards',
    `No .kicad_pcb file exists on "${ref}".`,
    'Pick a different branch, or check that the board is committed to this repository rather than ' +
      'kept elsewhere.',
  );
}

export function lfsPointer(path: string): RepoAccessError {
  return new RepoAccessError(
    'lfs',
    `"${path}" is stored in Git LFS, so the host returned a pointer file instead of the board.`,
    'Large File Storage content cannot be read through the plain file API. Fetch the file with ' +
      '`git lfs pull` and load it as a local file instead.',
  );
}

export function notABoard(path: string, head: string): RepoAccessError {
  return new RepoAccessError(
    'not-a-board',
    `"${path}" does not look like a KiCad board.`,
    `A .kicad_pcb file starts with "(kicad_pcb"; this one starts with "${head.slice(0, 40).replace(/\s+/g, ' ')}". ` +
      `The host may have returned an error page instead of the file.`,
  );
}

export function httpFailure(what: string, status: number, statusText: string): RepoAccessError {
  return new RepoAccessError(
    'http',
    `${what} failed: HTTP ${status}${statusText ? ` ${statusText}` : ''}.`,
    'The host answered, but not with what was expected. If this persists the API may have changed, ' +
      'or the repository may be in an unusual state.',
    status,
  );
}

/**
 * Turn any thrown value into a `RepoAccessError`. A raw `TypeError` from
 * `fetch` means the request never completed — see {@link unreachable}.
 */
export function asRepoError(err: unknown, host: string, provider?: string): RepoAccessError {
  if (err instanceof RepoAccessError) return err;
  if (err instanceof TypeError) return unreachable(host, provider);
  return new RepoAccessError(
    'http',
    `Reading from ${host} failed: ${(err as Error)?.message ?? String(err)}`,
    'This is unexpected. Loading the two files locally will always work.',
  );
}

/** Map a non-OK response to the most specific error we can justify. */
export function fromResponse(what: string, host: string, response: { status: number; statusText: string; headers: { get(n: string): string | null } }): RepoAccessError {
  if (response.status === 404) return notFound(what);
  if (response.status === 401) return privateRepo(host);
  if (response.status === 403 || response.status === 429) {
    const remaining = response.headers.get('x-ratelimit-remaining');
    const reset = response.headers.get('x-ratelimit-reset');
    if (response.status === 429 || remaining === '0') {
      const at = reset ? new Date(Number(reset) * 1000) : undefined;
      return rateLimited(host, at && Number.isFinite(at.getTime()) ? at : undefined);
    }
    return privateRepo(host);
  }
  return httpFailure(what, response.status, response.statusText);
}
